import { Sunrise, Sunset, Clock, CalendarDays, Check, MessageCircle } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { BATCHES, waLink } from "@/lib/site";

const THEMES = {
  morning: {
    Icon: Sunrise,
    card: "bg-white border-emerald-900/10",
    badge: "bg-[#D97706]/10 text-[#B45309]",
    icon: "bg-[#D97706]/10 text-[#D97706] group-hover:bg-[#D97706]",
    title: "text-slate-900",
    text: "text-slate-600",
    meta: "text-slate-700",
    check: "text-[#2E8B57]",
    cta: "bg-[#2E8B57] hover:bg-[#267a4b] text-white",
  },
  evening: {
    Icon: Sunset,
    card: "bg-[#0F2440] border-white/10",
    badge: "bg-white/10 text-emerald-200",
    icon: "bg-white/10 text-emerald-200 group-hover:bg-[#2E8B57]",
    title: "text-white",
    text: "text-slate-300",
    meta: "text-slate-200",
    check: "text-emerald-300",
    cta: "bg-white hover:bg-emerald-50 text-[#0F2440]",
  },
};

const BatchCard = ({ batch, delay }) => {
  const t = THEMES[batch.id];
  return (
    <Reveal delay={delay} className="h-full">
      <article
        data-testid={`batch-card-${batch.id}`}
        className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-24px_rgba(30,58,138,0.35)] sm:p-10 ${t.card}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className={`flex h-14 w-14 items-center justify-center rounded-2xl transition-colors duration-300 group-hover:text-white ${t.icon}`}>
            <t.Icon className="h-7 w-7" />
          </div>
          <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${t.badge}`}>{batch.mood}</span>
        </div>

        <h3 className={`font-heading mt-7 text-2xl font-semibold sm:text-3xl ${t.title}`}>{batch.name}</h3>

        <div className={`mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold ${t.meta}`}>
          <span data-testid={`batch-time-${batch.id}`} className="inline-flex items-center gap-2">
            <Clock className="h-4 w-4 opacity-70" />
            {batch.time}
          </span>
          <span data-testid={`batch-days-${batch.id}`} className="inline-flex items-center gap-2">
            <CalendarDays className="h-4 w-4 opacity-70" />
            {batch.days}
          </span>
        </div>

        <p className={`mt-5 text-sm leading-relaxed sm:text-base ${t.text}`}>{batch.description}</p>

        <ul className="mb-8 mt-6 space-y-2.5">
          {batch.forWhom.map((item) => (
            <li key={item} className={`flex items-center gap-3 text-sm ${t.meta}`}>
              <Check className={`h-4 w-4 shrink-0 ${t.check}`} />
              {item}
            </li>
          ))}
        </ul>

        <a
          data-testid={`batch-whatsapp-cta-${batch.id}`}
          href={waLink(`Namaste! I would like to join the ${batch.name} (${batch.time}) at NAVYOGA. Please share the details.`)}
          target="_blank"
          rel="noopener noreferrer"
          className={`mt-auto flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-[transform,background-color] duration-300 hover:-translate-y-0.5 ${t.cta}`}
        >
          <MessageCircle className="h-4 w-4" />
          Book {batch.name} on WhatsApp
        </a>
      </article>
    </Reveal>
  );
};

export const Schedule = () => (
  <section id="schedule" data-testid="schedule-section" className="relative bg-white py-20 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHeading
        testId="schedule-heading"
        overline="Group Class Schedule"
        title="Two batches a day — choose the light that suits you"
        description="Join our daily group yoga batches at the Port Blair center. Whether you love sunrise practice or need to unwind after work, there is a slot for you. Trial sessions available — just message us."
      />

      <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
        {BATCHES.map((batch, i) => (
          <BatchCard key={batch.id} batch={batch} delay={0.08 * i} />
        ))}
      </div>

      <Reveal delay={0.2}>
        <p data-testid="schedule-note" className="mt-8 text-center text-xs text-slate-500 sm:text-sm">
          Weekends are reserved for wellness camps and community sessions. Timings may shift slightly on festival days — confirm on WhatsApp.
        </p>
      </Reveal>
    </div>
  </section>
);
